import { Clickable, HighlightsSlider, Icon, Text } from 'components';
import { subDays } from 'date-fns';
import { theme } from 'lib';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Circle = styled.div`
  border-radius: 400px;
  background-color: #fff;
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
  border: 2px solid ${theme.GREY_10};
  height: ${(props) =>
    props.large ? '214px' : props.big ? '96px' : props.small ? '36px' : '68px'};
  width: ${(props) => (props.large ? '214px' : props.big ? '96px' : props.small ? '36px' : '68px')};

  ${(props) =>
    (props.online || props.streaming) &&
    `
    border-color: ${theme.GREEN};
  `};

  ${(props) =>
    (props.unseenStory || props.addStory) &&
    `
    border-color: ${theme.SECONDARY};
  `};

  ${(props) =>
    props.tiny &&
    `
    height: 24px;
    width: 24px;
    border: 0px;
  `};
`;

const Image = styled.div`
  background-color: ${(props) => (props.src ? '#fff' : theme.TALE)};
  border-radius: 100px;
  height: ${(props) =>
    props.large ? '204px' : props.big ? '84px' : props.small ? '32px' : '64px'};
  width: ${(props) => (props.large ? '204px' : props.big ? '84px' : props.small ? '32px' : '64px')};
  background-image: url('${(props) => props.src}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.tiny &&
    `
    height: 24px;
    width: 24px;
    margin: 0px;
  `};
`;

const IconContainer = styled.div`
  position: absolute;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background-color: #fff;
  border: 2px;
  border-color:  #fff;

  ${(props) =>
    props.play &&
    `
    bottom: -10px;
    right: ${props.small ? '24%' : props.big ? '40%' : '36%'};
  `};

  ${(props) =>
    props.addStory &&
    `
    bottom: 0;
    right: 0;
  `};

  ${(props) =>
    props.verified &&
    `
    bottom: ${props.big ? -3 : 0}px;
    right: ${props.big ? -3 : 0}px;
    background-color: transparent;
    border: 0;
  `}
`;

const Avatar = (props) => {
  const { user, streaming, addStory, tiny, small, big, large, showVerified } = props;
  const [showStory, setShowStory] = useState(false);
  const splitted = user?.name?.split(' '),
    initials =
      splitted &&
      `${splitted[0] && splitted[0][0].toUpperCase()}${splitted[1] && splitted[1][0].toUpperCase()}`.replace(
        'undefined',
        ''
      );
  const noAvatar = !props.avatar && !user?.avatar;
  const hasStory = user?.lastStoryAt && new Date(user.lastStoryAt) >= subDays(new Date(), 1);
  const clickDisabled = props.storiesSection || !hasStory;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isVerifiedAthlete = user?.type === 'athlete' && user?.verified?.id;

  function getText() {
    switch (true) {
      case !!tiny:
        return (
          <Text c2 bold>
            {initials}
          </Text>
        );

      case !!small:
        return (
          <Text c1 bold>
            {initials}
          </Text>
        );

      case !!big:
        return <Text h2>{initials}</Text>;

      case !!large:
        return (
          <Text size="55px" bold>
            {initials}
          </Text>
        );

      default:
        return (
          <Text h4 bold>
            {initials}
          </Text>
        );
    }
  }

  function getVerifiedSize() {
    switch (true) {
      case large:
        return 60;
      case big:
        return 40;
      case tiny:
      case small:
        return 18;
      default:
        return 22;
    }
  }

  function handleGetApp() {
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: {
        showGetApp: true
      }
    });
  }

  return (
    <React.Fragment>
      <HighlightsSlider
        userId={user?._id}
        visible={showStory}
        closeModal={() => setShowStory(false)}
      />

      <Clickable
        onClick={() => setShowStory(hasStory)}
        ogOpacity={clickDisabled}
        disabled={clickDisabled}
      >
        <Circle {...props} unseenStory={hasStory || props.unseenStory}>
          <Image
            {...props}
            resizeMode="contain"
            src={props.avatar || user?.avatar || null}
            noAvatar={noAvatar}
            key={props.avatar || user?.avatar}
          >
            {noAvatar && getText()}
          </Image>

          {streaming && (
            <IconContainer play {...props}>
              <Icon type="playCircle" col={theme.GREEN} dimensions={15} />
            </IconContainer>
          )}

          {addStory && (
            <IconContainer addStory {...props}>
              <Icon type="addCircle" col={theme.SECONDARY} dimensions={15} />
            </IconContainer>
          )}

          {showVerified && isVerifiedAthlete && (
            <IconContainer verified {...props}>
              <Icon type="verified" col={theme.PURPLE} dimensions={getVerifiedSize()} />
            </IconContainer>
          )}
        </Circle>
      </Clickable>
    </React.Fragment>
  );
};

export default Avatar;
